import React, { useState, useEffect, useRef } from 'react';
import './UserAccount.css';
import Sidebar from '../../components/Sidebar';
import SvgIcon from '../../components/SvgIcon';
import ProfileAccuracyWarningModal from './ProfileAccuracyWarningModal';

const UserAccount = ({ user, onLogout, onUserUpdate, onSwitchView }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [emailModal, setEmailModal] = useState(false);
  const [contactModal, setContactModal] = useState(false);
  const [orgModal, setOrgModal] = useState(false);
  const [supervisorModal, setSupervisorModal] = useState(false);

  // BCWA-101: Account Update Request workflow state
  const [accountUpdateRequest, setAccountUpdateRequest] = useState(null);
  const [dismissedRejection, setDismissedRejection] = useState(false);

  // Email modal state
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);

  // Contact modal state
  const [newContact, setNewContact] = useState('');
  const [contactError, setContactError] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);

  // Supervisor modal state
  const [newSupervisorName, setNewSupervisorName] = useState('');
  const [newSupervisorEmail, setNewSupervisorEmail] = useState('');
  const [supervisorError, setSupervisorError] = useState('');
  const [supervisorSubmitting, setSupervisorSubmitting] = useState(false);

  // Org modal state
  const [orgSector, setOrgSector] = useState('');
  const [orgSearchTerm, setOrgSearchTerm] = useState('');
  const [orgList, setOrgList] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isCreatingNewOrg, setIsCreatingNewOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [projectCode, setProjectCode] = useState('');
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgSubmitting, setOrgSubmitting] = useState(false);
  const [orgError, setOrgError] = useState('');
  const orgDropdownRef = useRef(null);

  // Email domain auto-matching state
  const [emailDomainMatch, setEmailDomainMatch] = useState(null);
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const emailDomainTimerRef = useRef(null);

  // Add Project Code state
  const [newProjectCodeInput, setNewProjectCodeInput] = useState('');
  const [addingProjectCode, setAddingProjectCode] = useState(false);
  const [projectCodeError, setProjectCodeError] = useState('');

  // Admin Full Name modal state
  const [fullNameModal, setFullNameModal] = useState(false);
  const [newFullName, setNewFullName] = useState('');
  const [fullNameError, setFullNameError] = useState('');
  const [fullNameSubmitting, setFullNameSubmitting] = useState(false);

  // Professional background & onboarding completion state
  const [itBackgroundVal, setItBackgroundVal] = useState('');
  const [experienceYearsVal, setExperienceYearsVal] = useState('');
  const [showAccuracyModal, setShowAccuracyModal] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const [lastLoginTime] = useState(() => {
    const now = new Date();
    return now.toLocaleDateString('en-GB') + ' ' + now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  });

  const formatDateTimeString = (dateInput) => {
    if (!dateInput) return lastLoginTime;
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return lastLoginTime;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${day}/${month}/${year} ${timeStr}`;
  };

  const formatExpiryDate = (d) => {
    if (!d) return 'N/A';
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) {
      const [y, m, day] = d.split('T')[0].split('-');
      return `${day}/${m}/${y}`;
    }
    try {
      const dateObj = new Date(d);
      if (isNaN(dateObj.getTime())) return String(d);
      return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return String(d);
    }
  };

  useEffect(() => {
    if (user?.email) fetchProfile(user.email);
  }, [user]);

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }
  }, [toast]);

  useEffect(() => {
    const handler = (e) => { if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target)) setOrgDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (emailDomainTimerRef.current) {
      clearTimeout(emailDomainTimerRef.current);
    }

    const email = newEmail.trim();
    const atIndex = email.indexOf('@');

    if (atIndex === -1 || atIndex === email.length - 1) {
      setEmailDomainMatch(null);
      setIsCheckingDomain(false);
      return;
    }

    const domain = email.substring(atIndex + 1).toLowerCase();

    if (!domain.includes('.') || domain.endsWith('.') || domain.startsWith('.')) {
      setEmailDomainMatch(null);
      setIsCheckingDomain(false);
      return;
    }

    setIsCheckingDomain(true);

    emailDomainTimerRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`/api/organisations-by-domain/${encodeURIComponent(domain)}`);
        const data = await response.json();

        if (data.status === 'success' && data.data.length > 0) {
          const matchedOrgs = data.data;
          const matchedSector = data.sector;

          setEmailDomainMatch({
            domain,
            orgs: matchedOrgs,
            sector: matchedSector
          });

          if (orgSector !== matchedSector) {
            setOrgSector(matchedSector);
            setSelectedOrgId('');
            setSelectedOrg(null);
            setNewOrgName('');
            setIsCreatingNewOrg(false);
            setOrgSearchTerm('');
            fetchOrgs(matchedSector);
          }

          if (matchedOrgs.length === 1) {
            const org = matchedOrgs[0];
            setSelectedOrg(org);
            setSelectedOrgId(org.OrganisationID.toString());
            setIsCreatingNewOrg(false);
            setOrgSearchTerm('');
            setOrgDropdownOpen(false);
          } else {
            setSelectedOrg(null);
            setSelectedOrgId('');
            setOrgDropdownOpen(true);
          }
        } else {
          setEmailDomainMatch(null);
        }
      } catch (error) {
        console.error('Error checking email domain:', error);
        setEmailDomainMatch(null);
      } finally {
        setIsCheckingDomain(false);
      }
    }, 500);

    return () => {
      if (emailDomainTimerRef.current) {
        clearTimeout(emailDomainTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEmail]);

  const fetchAccountUpdateStatus = async (learnerId, email) => {
    try {
      const res = await fetch(`/api/profile/account-update-request/status?learnerId=${learnerId || ''}&email=${encodeURIComponent(email || '')}`);
      const data = await res.json();
      if (data.status === 'success' && data.hasRequest) {
        setAccountUpdateRequest(data.data);
      } else {
        setAccountUpdateRequest(null);
      }
    } catch (e) {
      console.error('Failed to fetch account update status', e);
    }
  };

  const fetchProfile = async (email) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/profile/${encodeURIComponent(email)}`);
      const data = await res.json();
      if (data.status === 'success') {
        setProfile(data.profile);
        const isYes = data.profile.itBackground === 'Yes' || data.profile.itBackground === 1 || data.profile.itBackground === true || data.profile.itBackground === '1';
        const isNo = data.profile.itBackground === 'No' || data.profile.itBackground === 0 || data.profile.itBackground === false || data.profile.itBackground === '0';
        setItBackgroundVal(isYes ? 'Yes' : isNo ? 'No' : '');
        setExperienceYearsVal(data.profile.experienceYears && data.profile.experienceYears !== 'N/A' ? data.profile.experienceYears : '');

        // Fetch latest account update request status for learners
        if (data.profile.accountType !== 'admin' && data.profile.role !== 'Trainer') {
          fetchAccountUpdateStatus(data.profile.learnerId, data.profile.email);
        }
      } else if (res.status === 403 || data.status === 'deactivated') {
        setToast({ type: 'error', msg: data.message || 'Your account has been deactivated. Please contact the administrator to reactivate your account.' });
        setTimeout(() => { if (onLogout) onLogout(); }, 2500);
      } else {
        setToast({ type: 'error', msg: data.message });
      }
    } catch { setToast({ type: 'error', msg: 'Failed to connect to server.' }); }
    setLoading(false);
  };

  const fetchOrgs = async (sector) => {
    try {
      const res = await fetch(`/api/organisations/${sector.toLowerCase()}`);
      const data = await res.json();
      if (data.status === 'success') setOrgList(data.data);
    } catch { console.error('Failed to fetch orgs'); }
  };

  const openEmailModal = () => {
    setNewEmail('');
    setConfirmEmail('');
    setEmailError('');
    setEmailDomainMatch(null);
    setIsCheckingDomain(false);
    const currentSector = profile?.sector || '';
    setOrgSector(currentSector);
    setOrgSearchTerm('');
    if (profile?.organisationId) {
      setSelectedOrg({
        OrganisationID: profile.organisationId,
        OrganisationName: profile.organisationName
      });
      setSelectedOrgId(profile.organisationId.toString());
      setIsCreatingNewOrg(false);
      setNewOrgName('');
    } else {
      setSelectedOrg(null);
      setSelectedOrgId('');
      setIsCreatingNewOrg(false);
      setNewOrgName('');
    }
    setProjectCode(profile?.sector === 'Special Project' ? profile?.specialProjectCode || '' : '');
    if (['Government', 'Private', 'Student'].includes(currentSector)) {
      fetchOrgs(currentSector);
    }
    setEmailModal(true);
  };

  const openFullNameModal = () => {
    setNewFullName(profile?.fullName || '');
    setFullNameError('');
    setFullNameModal(true);
  };

  const openOrgModal = () => {
    setNewEmail('');
    setConfirmEmail('');
    setOrgError('');
    setEmailDomainMatch(null);
    setIsCheckingDomain(false);
    const currentSector = profile?.sector || '';
    setOrgSector(currentSector);
    setOrgSearchTerm('');
    if (profile?.organisationId) {
      setSelectedOrg({
        OrganisationID: profile.organisationId,
        OrganisationName: profile.organisationName
      });
      setSelectedOrgId(profile.organisationId.toString());
      setIsCreatingNewOrg(false);
      setNewOrgName('');
    } else {
      setSelectedOrg(null);
      setSelectedOrgId('');
      setIsCreatingNewOrg(false);
      setNewOrgName('');
    }
    setProjectCode(profile?.sector === 'Special Project' ? profile?.specialProjectCode || '' : '');
    if (['Government', 'Private', 'Student'].includes(currentSector)) {
      fetchOrgs(currentSector);
    }
    setOrgModal(true);
  };

  const submitProfileChanges = async (isEmailModal) => {
    const setError = isEmailModal ? setEmailError : setOrgError;
    const setSubmitting = isEmailModal ? setEmailSubmitting : setOrgSubmitting;
    const setModalOpen = isEmailModal ? setEmailModal : setOrgModal;

    setError('');

    const hasEmailChange = newEmail.trim() !== '' || confirmEmail.trim() !== '';
    let updatedEmail = profile.email;

    // 1. Validate Email if user entered something
    if (hasEmailChange) {
      if (!newEmail || !confirmEmail) {
        setError('All email fields are required.');
        return;
      }
      if (newEmail !== confirmEmail) {
        setError('Email addresses do not match.');
        return;
      }
      if (newEmail === profile.email) {
        setError('New email must be different from current.');
        return;
      }
    }

    // 2. Validate Organisation Info
    const hasSectorChange = orgSector !== profile.sector;
    let hasOrgDetailsChange = false;
    if (['Government', 'Private', 'Student'].includes(orgSector)) {
      if (isCreatingNewOrg) {
        hasOrgDetailsChange = newOrgName.trim().toUpperCase() !== (profile.organisationName || '').trim().toUpperCase();
      } else {
        hasOrgDetailsChange = selectedOrgId !== (profile.organisationId?.toString() || '');
      }
    }
    const isStaffAccount = profile?.accountType === 'admin' || Boolean(profile?.adminId) || profile?.role === 'Admin' || profile?.role === 'Editor' || profile?.role === 'Report Viewer' || profile?.role === 'ReportViewer';
    const hasOrgChange = !isStaffAccount && (hasSectorChange || hasOrgDetailsChange);

    if (hasOrgChange) {
      if (!orgSector) {
        setError('Sector is required.');
        return;
      }
      if (['Government', 'Private', 'Student'].includes(orgSector)) {
        if (isCreatingNewOrg) {
          if (!newOrgName.trim()) {
            setError('Organisation name is required.');
            return;
          }
        } else {
          if (!selectedOrgId) {
            setError('Please select an organisation.');
            return;
          }
        }
      } else if (orgSector === 'Special Project') {
        if (!projectCode.trim()) {
          setError('Project code is required.');
          return;
        }
      }
    }

    if (!hasEmailChange && !hasOrgChange) {
      setError('No changes detected.');
      return;
    }

    setSubmitting(true);

    try {
      if (isStaffAccount) {
        // Staff accounts (Admin / Editor): maintain direct update
        if (hasEmailChange) {
          const emailRes = await fetch('/api/profile/email', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ currentEmail: profile.email, newEmail })
          });
          const emailData = await emailRes.json();
          if (!emailRes.ok) {
            setError(emailData.message || 'Failed to update email.');
            setSubmitting(false);
            return;
          }
          updatedEmail = newEmail;
        }

        setToast({ type: 'success', msg: 'Profile updated successfully.' });
        if (hasEmailChange && onUserUpdate) {
          onUserUpdate({ ...user, email: updatedEmail });
        }
        fetchProfile(updatedEmail);
        setModalOpen(false);
        setNewEmail('');
        setConfirmEmail('');
      } else {
        // Learner accounts: Submit for Admin Approval (BCWA-101)
        const payload = {
          learnerId: profile.learnerId,
          currentEmail: profile.email,
          requestedEmail: hasEmailChange ? newEmail.trim() : null,
          requestedSector: hasOrgChange ? orgSector : null,
          requestedOrganisationId: (hasOrgChange && !isCreatingNewOrg && selectedOrgId) ? selectedOrgId : null,
          requestedNewOrganisationName: (hasOrgChange && isCreatingNewOrg) ? newOrgName.trim() : null,
          projectCode: (hasOrgChange && orgSector === 'Special Project') ? projectCode.trim() : null
        };

        const res = await fetch('/api/profile/account-update-request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await res.json();
        if (!res.ok) {
          setError(data.message || 'Failed to submit account-update request.');
          setSubmitting(false);
          return;
        }

        setToast({
          type: 'success',
          msg: data.message || 'Account-update request submitted! Changes will take effect after administrator approval.'
        });
        setModalOpen(false);
        setNewEmail('');
        setConfirmEmail('');
        fetchAccountUpdateStatus(profile.learnerId, profile.email);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to connect to server.');
    }
    setSubmitting(false);
  };

  const handleEmailUpdate = async () => {
    await submitProfileChanges(true);
  };

  const handleFullNameUpdate = async () => {
    setFullNameError('');
    if (!newFullName.trim()) {
      setFullNameError('Full name is required.');
      return;
    }
    if (newFullName.trim() === profile.fullName) {
      setFullNameError('New full name must be different from current.');
      return;
    }

    setFullNameSubmitting(true);
    try {
      const res = await fetch('/api/profile/admin', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentEmail: profile.email,
          username: profile.username,
          fullName: newFullName.trim(),
          email: profile.email
        })
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ type: 'success', msg: 'Full name updated successfully.' });
        if (onUserUpdate) {
          onUserUpdate({ ...user, fullName: newFullName.trim() });
        }
        fetchProfile(profile.email);
        setFullNameModal(false);
      } else {
        setFullNameError(data.message || 'Failed to update full name.');
      }
    } catch (err) {
      console.error(err);
      setFullNameError('Failed to connect to server.');
    }
    setFullNameSubmitting(false);
  };

  const handleOrgUpdate = async () => {
    await submitProfileChanges(false);
  };

  const handleContactUpdate = async () => {
    setContactError('');
    if (!newContact.trim()) { setContactError('Contact number is required.'); return; }
    // Basic Malaysian phone validation: 01X-XXXXXXX or 01X-XXXXXXXX
    const phoneRegex = /^(01)[0-9]{1}-[0-9]{7,8}$|^(01)[0-9]{8,9}$/;
    if (!phoneRegex.test(newContact.replace(/\s/g, ''))) {
      setContactError('Please enter a valid Malaysian phone number (e.g. 012-3456789).');
      return;
    }
    if (newContact.trim() === profile.contact) { setContactError('New contact number must be different from current.'); return; }
    setContactSubmitting(true);
    try {
      const res = await fetch('/api/profile/contact', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email, newContact: newContact.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ type: 'success', msg: 'Contact number updated successfully.' });
        fetchProfile(profile.email);
        setContactModal(false); setNewContact('');
      } else { setContactError(data.message); }
    } catch { setContactError('Failed to connect to server.'); }
    setContactSubmitting(false);
  };

  const handleSupervisorUpdate = async () => {
    setSupervisorError('');
    if (!newSupervisorName.trim() && !newSupervisorEmail.trim()) {
      setSupervisorError('At least one field is required.');
      return;
    }
    
    // Email validation if provided
    if (newSupervisorEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newSupervisorEmail)) {
      setSupervisorError('Please enter a valid email address.');
      return;
    }

    setSupervisorSubmitting(true);
    try {
      const res = await fetch('/api/profile/supervisor', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: profile.email, 
          supervisorName: newSupervisorName.trim(),
          supervisorEmail: newSupervisorEmail.trim()
        })
      });
      const data = await res.json();
      if (res.ok) {
        setToast({ type: 'success', msg: 'Supervisor details updated successfully.' });
        fetchProfile(profile.email);
        setSupervisorModal(false); 
      } else { 
        setSupervisorError(data.message); 
      }
    } catch { 
      setSupervisorError('Failed to connect to server.'); 
    }
    setSupervisorSubmitting(false);
  };

  const handleSaveClick = () => {
    if (!itBackgroundVal) {
      setToast({ type: 'error', msg: 'Please indicate your IT Background (Yes/No).' });
      return;
    }
    if (!experienceYearsVal) {
      setToast({ type: 'error', msg: 'Please specify your Cybersecurity / Technical Experience level.' });
      return;
    }

    // Suppress immediate submission and open confirmation modal
    setShowAccuracyModal(true);
  };

  const handleConfirmSaveProfile = async () => {
    setIsSavingProfile(true);
    try {
      const res = await fetch('/api/learner/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: profile.email,
          learnerId: profile.learnerId,
          itBackground: itBackgroundVal,
          experienceYears: experienceYearsVal,
          mustCompleteProfile: 0
        })
      });

      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setToast({ type: 'success', msg: 'Profile updated and onboarding completed successfully!' });
        setShowAccuracyModal(false);
        setProfile(prev => ({
          ...prev,
          itBackground: itBackgroundVal,
          experienceYears: experienceYearsVal,
          mustCompleteProfile: false
        }));
        if (onUserUpdate) {
          onUserUpdate({
            ...user,
            mustCompleteProfile: false
          });
        }
        fetchProfile(profile.email);
      } else {
        setToast({ type: 'error', msg: data.message || 'Failed to save profile changes.' });
      }
    } catch (err) {
      console.error('Save profile error:', err);
      setToast({ type: 'error', msg: 'Failed to connect to server.' });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const [unlockedBanner, setUnlockedBanner] = useState(null);

  const handleAddProjectCode = async () => {
    setProjectCodeError('');
    if (!newProjectCodeInput.trim()) {
      setProjectCodeError('Please enter a project code.');
      return;
    }
    
    setAddingProjectCode(true);
    try {
      const res = await fetch('/api/profile/project-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: profile.email, projectCode: newProjectCodeInput.trim() })
      });
      
      const data = await res.json();
      if (res.ok) {
        setToast({ type: 'success', msg: data.message || 'Project code added successfully.' });
        if (data.tag || (data.unlockedCourses && data.unlockedCourses.length > 0)) {
          setUnlockedBanner({
            tag: data.tag,
            courses: data.unlockedCourses || []
          });
        }
        fetchProfile(profile.email);
        setNewProjectCodeInput('');
      } else {
        setProjectCodeError(data.message || 'Failed to add project code.');
      }
    } catch (err) {
      setProjectCodeError('Failed to connect to server.');
    }
    setAddingProjectCode(false);
  };

  const handleOrgSelect = (org) => {
    if (org === 'CREATE_NEW') {
      setIsCreatingNewOrg(true);
      setSelectedOrg(null);
      setSelectedOrgId('');
      setNewOrgName('');
    } else {
      setSelectedOrg(org);
      setSelectedOrgId(org.OrganisationID.toString());
      setIsCreatingNewOrg(false);
      setNewOrgName('');
    }
    setOrgSearchTerm('');
    setOrgDropdownOpen(false);
  };

  const renderOrgSelectorFields = () => {
    if (!['Government', 'Private', 'Student'].includes(orgSector)) return null;

    let label = '';
    let placeholder = '';
    let newOrgPlaceholder = '';

    if (orgSector === 'Government') {
      label = 'Government Organisation';
      placeholder = 'Search government organisation...';
      newOrgPlaceholder = 'Enter new government organisation';
    } else if (orgSector === 'Private') {
      label = 'Company Name';
      placeholder = 'Search company...';
      newOrgPlaceholder = 'Enter new company name';
    } else if (orgSector === 'Student') {
      label = 'University / College Name';
      placeholder = 'Search university / college...';
      newOrgPlaceholder = 'Enter new institution name';
    }

    return (
      <>
        <div className="modal-field">
          <label>{label}</label>
          <div className="modal-search-dropdown-wrapper" ref={orgDropdownRef}>
            <input
              type="text"
              placeholder={placeholder}
              value={orgDropdownOpen ? orgSearchTerm : (selectedOrg ? selectedOrg.OrganisationName.toUpperCase() : (isCreatingNewOrg ? 'CREATING NEW...' : orgSearchTerm))}
              onChange={(e) => {
                const val = e.target.value.toUpperCase();
                setOrgSearchTerm(val);
                setOrgDropdownOpen(true);
                if (selectedOrg || isCreatingNewOrg) {
                  setSelectedOrg(null);
                  setSelectedOrgId('');
                  setIsCreatingNewOrg(false);
                  setNewOrgName('');
                }
              }}
              onFocus={() => setOrgDropdownOpen(true)}
            />
            {orgDropdownOpen && (
              <div className="modal-dropdown-list">
                {filteredOrgs.length > 0 ? (
                  filteredOrgs.map(org => (
                    <div
                      key={org.OrganisationID}
                      className={`modal-dropdown-item ${selectedOrgId === org.OrganisationID.toString() ? 'selected' : ''}`}
                      onClick={() => handleOrgSelect(org)}
                    >
                      {org.OrganisationName}
                    </div>
                  ))
                ) : (
                  <div className="modal-dropdown-no-results" style={{ cursor: 'default' }}>No organisations found</div>
                )}
                {filteredOrgs.length === 0 && (
                  <div
                    className="modal-dropdown-item create-new-item"
                    onClick={() => handleOrgSelect('CREATE_NEW')}
                    style={{ fontWeight: 'bold', color: '#3b28cc', borderTop: '1px solid #e2e8f0', cursor: 'pointer', padding: '8px 12px' }}
                  >
                    CREATE NEW?
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {isCreatingNewOrg && (
          <div className="modal-field" style={{ marginTop: '10px' }}>
            <label>New {label}</label>
            <input
              type="text"
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value.toUpperCase())}
              required
              placeholder={newOrgPlaceholder.toUpperCase()}
            />
          </div>
        )}
      </>
    );
  };

  const filteredOrgs = emailDomainMatch && emailDomainMatch.orgs.length > 0
    ? emailDomainMatch.orgs.filter(o => o.OrganisationName.toLowerCase().includes(orgSearchTerm.toLowerCase()))
    : orgList.filter(o => o.OrganisationName.toLowerCase().includes(orgSearchTerm.toLowerCase()));
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A';

  const isStaff = profile?.accountType === 'admin' ||
    Boolean(profile?.adminId) ||
    ['Admin', 'Editor', 'Report Viewer', 'ReportViewer'].includes(profile?.role) ||
    ['Admin', 'Editor', 'Report Viewer', 'ReportViewer'].includes(user?.role);

  if (!user) {
    return (
      <div className="profile-loading">
        <p>Please log in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="profile-page-layout">
      <Sidebar activeView="account" onSwitchView={onSwitchView} onLogout={onLogout} user={user} />

      {/* Main */}
      <div className="profile-main-content">
        <header className="profile-top-bar user-account-top-bar">
          <div className="profile-top-bar-left">
            <SvgIcon name="user" size={22} />
            <h1>My Profile</h1>
          </div>
          <div className="profile-top-bar-right">
            {!isStaff && (
              <>

                <div className="profile-top-bar-user" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div className="profile-top-bar-empty-avatar">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                  <span className="profile-top-bar-username">{profile?.fullName || user?.fullName}</span>
                </div>
              </>
            )}
          </div>
        </header>

        <div className="profile-content-body">
          {loading ? (
            <div className="profile-loading"><div className="profile-loading-spinner" /><p>Loading profile...</p></div>
          ) : profile ? (
            <>
              <div className="profile-page-header">
                <h2>My Profile</h2>
                <p>{isStaff ? 'View and manage your personal and account information.' : 'View and manage your personal and organisation information.'}</p>
              </div>

              {/* Onboarding Alert Banner for provisioned/bulk-imported accounts */}
              {Boolean(profile.mustCompleteProfile || user?.mustCompleteProfile || user?.MustCompleteProfile === 1) && (
                <div 
                  className="profile-onboarding-banner"
                  style={{
                    backgroundColor: '#eff6ff',
                    border: '1.5px solid #3b82f6',
                    borderRadius: '12px',
                    padding: '1.25rem 1.5rem',
                    marginBottom: '1.5rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.12)'
                  }}
                  data-testid="onboarding-profile-banner"
                >
                  <div style={{ color: '#2563eb', marginTop: '2px' }}>
                    <SvgIcon name="shield" size={24} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 0.25rem 0', color: '#1e3a8a', fontSize: '1rem', fontWeight: 700 }}>
                      Action Required: Complete Your Profile
                    </h4>
                    <p style={{ margin: 0, color: '#1e40af', fontSize: '0.875rem', lineHeight: 1.5 }}>
                      Your account was provisioned with essential details. Please complete your professional information below before registering for training courses.
                    </p>
                  </div>
                </div>
              )}

              {/* BCWA-101: Account Update Request Status Banners */}
              {!isStaff && accountUpdateRequest && accountUpdateRequest.Status === 'Pending' && (
                <div className="account-update-pending-banner" data-testid="pending-account-update-banner">
                  <div className="banner-icon">⏳</div>
                  <div className="banner-content">
                    <h4>Account Update Pending Admin Approval</h4>
                    <p>
                      You submitted an account update request on <strong>{formatDateTimeString(accountUpdateRequest.SubmittedAt)}</strong>.
                      The requested modifications will take effect only after administrator verification and approval.
                    </p>
                    <div className="pending-details-chips">
                      {accountUpdateRequest.RequestedEmail && (
                        <span className="chip" data-testid="chip-requested-email">
                          Requested Email: <strong>{accountUpdateRequest.RequestedEmail}</strong>
                        </span>
                      )}
                      {accountUpdateRequest.RequestedSector && (
                        <span className="chip" data-testid="chip-requested-sector">
                          Requested Sector: <strong>{accountUpdateRequest.RequestedSector}</strong>
                          {accountUpdateRequest.RequestedOrganisationName ? ` (${accountUpdateRequest.RequestedOrganisationName})` : ''}
                        </span>
                      )}
                      <span className={`chip chip-sla ${accountUpdateRequest.slaBadgeClass || 'badge-sla-normal'}`} data-testid="chip-sla-badge">
                        Review Turnaround: <strong>{accountUpdateRequest.slaBadgeText || 'In Progress'}</strong>
                      </span>
                    </div>
                    <p className="banner-subtext">
                      * Your currently approved login email (<strong>{profile.email}</strong>) and sector (<strong>{profile.sector}</strong>) remain active.
                    </p>
                  </div>
                </div>
              )}

              {!isStaff && accountUpdateRequest && accountUpdateRequest.Status === 'Rejected' && !dismissedRejection && (
                <div className="account-update-rejected-banner" data-testid="rejected-account-update-banner">
                  <div className="banner-icon">⚠️</div>
                  <div className="banner-content">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <h4>Recent Account Update Request Not Approved</h4>
                      <button
                        className="dismiss-banner-btn"
                        onClick={() => setDismissedRejection(true)}
                        data-testid="dismiss-rejection-btn"
                        title="Dismiss banner"
                      >
                        ✕
                      </button>
                    </div>
                    <p>
                      Your account-update request submitted on {formatDateTimeString(accountUpdateRequest.SubmittedAt)} was reviewed and rejected on <strong>{formatDateTimeString(accountUpdateRequest.ReviewedAt)}</strong>.
                    </p>
                    {accountUpdateRequest.RejectionReason && (
                      <div className="rejection-reason-box" data-testid="rejection-reason-text">
                        <strong>Reason for Rejection:</strong> {accountUpdateRequest.RejectionReason}
                      </div>
                    )}
                    <p className="banner-subtext">
                      Your existing approved email and sector remain active and unchanged. You may submit a revised update request if needed.
                    </p>
                  </div>
                </div>
              )}

              {/* High-Fidelity Split-Column Layout */}
              <div className="profile-body-grid">

                
                {/* Left Column - Form & Details */}
                <div className="profile-left-column">
                  
                  {/* Personal Information */}
                  <div className="profile-section-card">
                    <div className="section-card-header">
                      <div className="section-card-icon"><SvgIcon name="user" size={18} /></div>
                      <h4 className="section-card-title">Personal Information</h4>
                    </div>
                    <div className="info-field-row">
                      <span className="info-field-label">Full Name</span>
                      <span className="info-field-value">{profile.fullName || 'N/A'}</span>
                      {isStaff && (
                        <div className="info-field-action">
                          <button className="edit-btn" onClick={openFullNameModal}>
                            <SvgIcon name="edit" size={13} /> Edit
                          </button>
                        </div>
                      )}
                    </div>
                    {profile.role !== 'Trainer' && (
                      <div className="info-field-row">
                        <span className="info-field-label">Username</span>
                        <span className="info-field-value">{profile.username || 'N/A'}</span>
                      </div>
                    )}
                    <div className="info-field-row">
                      <span className="info-field-label">Email Address</span>
                      <span className="info-field-value">{profile.email}</span>
                      <div className="info-field-action">
                        <button className="edit-btn" onClick={openEmailModal}>
                          <SvgIcon name="edit" size={13} /> Edit
                        </button>
                      </div>
                    </div>
                    {isStaff ? (
                      <>
                        <div className="info-field-row">
                          <span className="info-field-label">Role</span>
                          <span className="info-field-value">{profile.adminRole || profile.role || 'Admin'}</span>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">Account Status</span>
                          <span className="info-field-value">
                            <span style={{
                              backgroundColor: '#ecfdf5',
                              color: '#10b981',
                              fontSize: '12px',
                              fontWeight: '600',
                              padding: '2px 8px',
                              borderRadius: '10px'
                            }}>
                              Active
                            </span>
                          </span>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">Member Since</span>
                          <span className="info-field-value">22/06/2026</span>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">Last Login</span>
                          <span className="info-field-value">{lastLoginTime}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="info-field-row">
                          <span className="info-field-label">Contact Number</span>
                          <span className="info-field-value">{profile.contact || 'N/A'}</span>
                          <div className="info-field-action">
                            <button className="edit-btn" onClick={() => { setContactModal(true); setNewContact(profile.contact || ''); setContactError(''); }}>
                              <SvgIcon name="edit" size={13} /> Edit
                            </button>
                          </div>
                        </div>
                        <div className="info-field-row">
                          <span className="info-field-label">Gender</span>
                          <span className="info-field-value">{profile.gender || 'N/A'}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Organisation Information */}
                  {!isStaff && (
                    <div className="profile-section-card">
                      <div className="section-card-header">
                        <div className="section-card-icon"><SvgIcon name="building" size={18} /></div>
                        <h4 className="section-card-title">Organisation Information</h4>
                      </div>
                      <div className="info-field-row"><span className="info-field-label">Sector</span><span className="info-field-value">{profile.sector}</span></div>
                      <div className="info-field-row">
                        <span className="info-field-label">Organisation</span>
                        <span className="info-field-value">{profile.organisationName}</span>
                        <div className="info-field-action">
                          <button className="edit-btn" onClick={openOrgModal}><SvgIcon name="edit" size={13} /> Edit</button>
                        </div>
                      </div>
                      <div className="info-field-row"><span className="info-field-label">Position</span><span className="info-field-value">{profile.position || 'N/A'}</span></div>
                      
                      {/* IT Background Yes/No Toggle */}
                      <div className="info-field-row" style={{ alignItems: 'center' }}>
                        <span className="info-field-label">IT Background</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <button
                            type="button"
                            className={`btn-toggle-option ${itBackgroundVal === 'Yes' ? 'active' : ''}`}
                            onClick={() => setItBackgroundVal('Yes')}
                            style={{
                              padding: '0.35rem 0.85rem',
                              borderRadius: '6px',
                              border: `1.5px solid ${itBackgroundVal === 'Yes' ? '#4f46e5' : '#cbd5e1'}`,
                              backgroundColor: itBackgroundVal === 'Yes' ? '#4f46e5' : '#ffffff',
                              color: itBackgroundVal === 'Yes' ? '#ffffff' : '#475569',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              cursor: 'pointer'
                            }}
                            data-testid="it-background-yes-btn"
                          >
                            Yes
                          </button>
                          <button
                            type="button"
                            className={`btn-toggle-option ${itBackgroundVal === 'No' ? 'active' : ''}`}
                            onClick={() => setItBackgroundVal('No')}
                            style={{
                              padding: '0.35rem 0.85rem',
                              borderRadius: '6px',
                              border: `1.5px solid ${itBackgroundVal === 'No' ? '#4f46e5' : '#cbd5e1'}`,
                              backgroundColor: itBackgroundVal === 'No' ? '#4f46e5' : '#ffffff',
                              color: itBackgroundVal === 'No' ? '#ffffff' : '#475569',
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              cursor: 'pointer'
                            }}
                            data-testid="it-background-no-btn"
                          >
                            No
                          </button>
                          {Boolean(profile.mustCompleteProfile || user?.mustCompleteProfile) && !itBackgroundVal && (
                            <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>* Required</span>
                          )}
                        </div>
                      </div>

                      {/* Experience Level Dropdown */}
                      <div className="info-field-row" style={{ alignItems: 'center' }}>
                        <span className="info-field-label">Experience Level</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <select
                            value={experienceYearsVal}
                            onChange={(e) => setExperienceYearsVal(e.target.value)}
                            data-testid="experience-years-select"
                            style={{
                              padding: '0.4rem 0.75rem',
                              borderRadius: '6px',
                              border: `1.5px solid ${Boolean(profile.mustCompleteProfile || user?.mustCompleteProfile) && !experienceYearsVal ? '#f59e0b' : '#cbd5e1'}`,
                              backgroundColor: '#ffffff',
                              fontSize: '0.85rem',
                              color: '#1e293b'
                            }}
                          >
                            <option value="">Select Experience (Years)</option>
                            <option value="0-2 years">0 - 2 years</option>
                            <option value="3-5 years">3 - 5 years</option>
                            <option value="6-10 years">6 - 10 years</option>
                            <option value="10+ years">10+ years</option>
                            <option value="Not Applicable">Not Applicable</option>
                          </select>
                          {Boolean(profile.mustCompleteProfile || user?.mustCompleteProfile) && !experienceYearsVal && (
                            <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>* Required</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Supervisor Details */}
                  {!isStaff && (
                    <div className="profile-section-card">
                      <div className="section-card-header">
                        <div className="section-card-icon"><SvgIcon name="users" size={18} /></div>
                        <h4 className="section-card-title" style={{ flex: 1 }}>Supervisor Details</h4>
                        <button className="edit-btn" onClick={() => { 
                          setSupervisorModal(true); 
                          setNewSupervisorName(profile.supervisorName === 'N/A' ? '' : (profile.supervisorName || '')); 
                          setNewSupervisorEmail(profile.supervisorEmail === 'N/A' ? '' : (profile.supervisorEmail || '')); 
                          setSupervisorError(''); 
                        }} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', backgroundColor: 'transparent', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500 }}>
                          <SvgIcon name="edit" size={13} /> Edit
                        </button>
                      </div>
                      <div className="info-field-row"><span className="info-field-label">Name</span><span className="info-field-value">{profile.supervisorName || 'N/A'}</span></div>
                      <div className="info-field-row"><span className="info-field-label">Email Address</span><span className="info-field-value">{profile.supervisorEmail || 'N/A'}</span></div>
                    </div>
                  )}



                  {/* Training Information - Commented out due to incorrect details */}
                  {/*
                  <div className="profile-section-card">
                    <div className="section-card-header">
                      <div className="section-card-icon"><SvgIcon name="book" size={18} /></div>
                      <h4 className="section-card-title">Training Information</h4>
                    </div>
                    <div className="info-field-row"><span className="info-field-label">Registration ID</span><span className="info-field-value font-mono">{profile.registrationId}</span></div>
                    <div className="info-field-row"><span className="info-field-label">Intake Session</span><span className="info-field-value">{profile.intakeSession}</span></div>
                    <div className="info-field-row"><span className="info-field-label">Registration Date</span><span className="info-field-value">{formatDate(profile.registrationDate)}</span></div>
                    <div className="info-field-row">
                      <span className="info-field-label">Registration Status</span>
                      <span className="info-field-value">
                        <span className={`status-badge ${profile.registrationStatus?.toLowerCase() || 'approved'}`}>
                          {profile.registrationStatus || 'Approved'}
                        </span>
                      </span>
                    </div>
                  </div>
                  */}

                  {/* Form Footer Action Buttons */}
                  {!isStaff && (
                    <div className="profile-form-footer-actions">
                      <button 
                        className="btn-save-changes" 
                        onClick={handleSaveClick}
                        data-testid="profile-save-changes-btn"
                      >
                        Save Changes
                      </button>
                      <button className="btn-cancel-changes" onClick={() => fetchProfile(profile.email)}>
                        Cancel
                      </button>
                    </div>
                  )}

                </div>

                {/* Right Column - Avatar & Metadata */}
                <div className="profile-right-column">
                  
                  {/* Premium User Summary Card */}
                  <div className="profile-summary-side-card">
                    <div className="profile-avatar-empty-circle">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                      </svg>
                    </div>
                    
                    <div className="profile-summary-side-details">
                      <h3>{profile.fullName}</h3>
                      <span className="profile-summary-role-badge">
                        {isStaff ? (profile.adminRole || profile.role || 'Administrator') : 'Learner'}
                      </span>
                    </div>

                    <div className="profile-summary-side-metadata">
                      <div className="metadata-row">
                        <span className="metadata-label">Member since</span>
                        <span className="metadata-value">
                          {isStaff ? '22/06/2026' : formatDate(profile.createdAt)}
                        </span>
                      </div>
                      <div className="metadata-row">
                        <span className="metadata-label">Last login</span>
                        <span className="metadata-value">
                          {profile.lastLoginDate ? formatDateTimeString(profile.lastLoginDate) : lastLoginTime}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Special Project Code Card */}
                  {!isStaff && (
                    <div className="profile-summary-side-card project-code-side-card" style={{ marginTop: '1.5rem' }}>
                      <div className="project-code-header" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                        <div style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', padding: '0.5rem', borderRadius: '50%', display: 'flex', color: '#6366f1' }}>
                          <SvgIcon name="clipboard" size={18} />
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b' }}>Special Project</h3>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                        Have a project code from BlackBerry? Add it here to unlock specific courses.
                      </p>

                      {((profile.assignedTags && profile.assignedTags.length > 0) || (profile.specialProjectCode && profile.specialProjectCode !== 'N/A')) && (
                        <div data-testid="current-project-tag-badge" style={{
                          marginBottom: '1rem',
                          padding: '10px 14px',
                          backgroundColor: '#eef2ff',
                          border: '1px solid #c7d2fe',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem'
                        }}>
                          <div style={{
                            display: 'flex',
                            flexDirection: profile.assignedTags && profile.assignedTags.length > 1 ? 'column' : 'row',
                            alignItems: profile.assignedTags && profile.assignedTags.length > 1 ? 'flex-start' : 'center',
                            justifyContent: 'space-between',
                            gap: '0.5rem'
                          }}>
                            <span style={{ fontSize: '0.8rem', color: '#3730a3', fontWeight: 600 }}>
                              {profile.assignedTags && profile.assignedTags.length > 1 ? 'Active Project Tags:' : 'Active Project Tag:'}
                            </span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {profile.assignedTags && profile.assignedTags.length > 0 ? (
                                profile.assignedTags.map((t, idx) => (
                                  <span 
                                    key={t.UserTagID || idx} 
                                    style={{ 
                                      fontFamily: 'monospace', 
                                      fontWeight: 800, 
                                      color: t.isExpired ? '#991b1b' : '#1e1b4b', 
                                      backgroundColor: t.isExpired ? '#fee2e2' : '#e0e7ff',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontSize: '0.85rem',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    {t.TagCode}
                                    {t.isExpired && <span style={{ fontSize: '0.65rem', textTransform: 'uppercase', fontWeight: 700 }}>(Expired)</span>}
                                  </span>
                                ))
                              ) : (
                                <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#1e1b4b', fontSize: '0.85rem' }}>
                                  {profile.specialProjectCode}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Display project tag expiry date */}
                          {profile.assignedTags && profile.assignedTags.length > 0 ? (
                            profile.assignedTags.map((t, idx) => (
                              t.ExpiryDate && (
                                <div 
                                  key={`expiry-${t.UserTagID || idx}`}
                                  data-testid="project-tag-expiry"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    paddingTop: '6px',
                                    borderTop: '1px dashed #c7d2fe',
                                    fontSize: '0.78rem'
                                  }}
                                >
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#4338ca', fontWeight: 600 }}>
                                    <SvgIcon name="calendar" size={13} />
                                    <span>{profile.assignedTags.length > 1 ? `${t.TagCode} Expiry Date:` : 'Tag Expiry Date:'}</span>
                                  </span>
                                  <span style={{ fontWeight: 700, color: t.isExpired ? '#991b1b' : '#1e1b4b', fontFamily: 'monospace' }}>
                                    {formatExpiryDate(t.ExpiryDate)}
                                  </span>
                                </div>
                              )
                            ))
                          ) : (
                            profile.tagExpiryDate && (
                              <div 
                                data-testid="project-tag-expiry"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  paddingTop: '6px',
                                  borderTop: '1px dashed #c7d2fe',
                                  fontSize: '0.78rem'
                                }}
                              >
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#4338ca', fontWeight: 600 }}>
                                  <SvgIcon name="calendar" size={13} />
                                  <span>Tag Expiry Date:</span>
                                </span>
                                <span style={{ fontWeight: 700, color: '#1e1b4b', fontFamily: 'monospace' }}>
                                  {formatExpiryDate(profile.tagExpiryDate)}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      )}

                      <div className="project-code-input-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <input 
                          type="text" 
                          placeholder="E.G. SP-2025-00123" 
                          value={newProjectCodeInput}
                          onChange={(e) => setNewProjectCodeInput(e.target.value.toUpperCase())}
                          data-testid="account-project-code-input"
                          style={{
                            width: '100%',
                            padding: '0.75rem 1rem',
                            border: `1px solid ${projectCodeError ? '#ef4444' : '#cbd5e1'}`,
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            outline: 'none',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                            backgroundColor: '#fff'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#6366f1'}
                          onBlur={(e) => e.target.style.borderColor = projectCodeError ? '#ef4444' : '#cbd5e1'}
                        />
                        {projectCodeError && <p style={{ color: '#ef4444', fontSize: '0.8rem', margin: 0 }}>{projectCodeError}</p>}
                        <button 
                          onClick={handleAddProjectCode}
                          disabled={addingProjectCode}
                          data-testid="add-project-code-btn"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            backgroundColor: '#3b28cc',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: addingProjectCode ? 'not-allowed' : 'pointer',
                            opacity: addingProjectCode ? 0.7 : 1,
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => { if (!addingProjectCode) e.target.style.backgroundColor = '#2e1fa0'; }}
                          onMouseOut={(e) => { if (!addingProjectCode) e.target.style.backgroundColor = '#3b28cc'; }}
                        >
                          {addingProjectCode ? 'Adding...' : 'Add Project Code'}
                        </button>
                      </div>

                      {unlockedBanner && (
                        <div data-testid="unlocked-courses-banner" style={{
                          marginTop: '1rem',
                          padding: '12px 14px',
                          borderRadius: '8px',
                          backgroundColor: '#ecfdf5',
                          border: '1px solid #86efac',
                          color: '#166534',
                          fontSize: '0.85rem'
                        }}>
                          <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <SvgIcon name="checkCircle" size={16} />
                              <span>Tag Unlocked: {unlockedBanner.tag?.tagCode || unlockedBanner.tag?.TagCode || 'Special Project'}</span>
                            </div>
                            {(unlockedBanner.tag?.expiryDate || unlockedBanner.tag?.ExpiryDate) && (
                              <span data-testid="unlocked-banner-expiry" style={{ fontSize: '0.725rem', fontWeight: 600, color: '#15803d', backgroundColor: '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>
                                Expires: {formatExpiryDate(unlockedBanner.tag?.expiryDate || unlockedBanner.tag?.ExpiryDate)}
                              </span>
                            )}
                          </div>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: '#15803d' }}>
                            You now have access to {(unlockedBanner.courses || []).length} exclusive course(s):
                          </p>
                          <ul style={{ margin: '6px 0 0 16px', padding: 0, fontSize: '0.8rem' }}>
                            {(unlockedBanner.courses || []).map(c => (
                              <li key={c.CourseID} style={{ fontWeight: 600 }}>{c.CertificationBody} {c.CourseName}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Special Project Disclaimer */}
                      <div data-testid="special-project-disclaimer" style={{
                        marginTop: '1.25rem',
                        padding: '12px 14px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'flex-start'
                      }}>
                        <div style={{ color: '#6366f1', marginTop: '2px', flexShrink: 0 }}>
                          <SvgIcon name="info" size={16} />
                        </div>
                        <div style={{ fontSize: '0.78rem', color: '#475569', lineHeight: 1.55 }}>
                          <strong style={{ color: '#1e293b', display: 'block', marginBottom: '3px' }}>
                            Important Notice & Course Access Rule:
                          </strong>
                          Once an active project tag is assigned to your account, you can only view and register for the specific courses allocated under that project tag. All courses outside of the project tag are hidden until the project tag expires. Upon expiration, the project tag is automatically unassigned from your account, and access to all standard courses will be restored.
                        </div>
                      </div>
                    </div>
                  )}


                </div>

              </div>
            </>
          ) : (
            <div className="profile-loading"><p>Could not load profile data.</p></div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`profile-toast ${toast.type}`}>
          <div className="profile-toast-icon"><SvgIcon name={toast.type === 'success' ? 'checkCircle' : 'alertCircle'} size={20} /></div>
          <div className="profile-toast-content">
            <span className="profile-toast-title">{toast.type === 'success' ? 'Success!' : 'Error'}</span>
            <span className="profile-toast-desc">{toast.msg}</span>
          </div>
          <button className="profile-toast-close" onClick={() => setToast(null)}><SvgIcon name="x" size={16} /></button>
        </div>
      )}

      {/* Email Modal */}
      {emailModal && (
        <div className="profile-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setEmailModal(false); }}>
          <div className="profile-modal-card">
            <div className="modal-header">
              <h3>{isStaff ? 'Edit Email Address' : 'Edit Email & Organisation'}</h3>
              <button className="modal-close-btn" onClick={() => setEmailModal(false)}><SvgIcon name="x" size={20} /></button>
            </div>
            <div className="modal-body">
              {!isStaff && (
                accountUpdateRequest?.Status === 'Pending' ? (
                  <div className="account-update-modal-notice warning" data-testid="modal-pending-warning">
                    <span>⚠️</span>
                    <div>
                      <strong>Pending Request Under Review:</strong> You already have an account-update request submitted on {formatDateTimeString(accountUpdateRequest.SubmittedAt)} awaiting administrator review. Submitting another change is disabled until processed.
                    </div>
                  </div>
                ) : (
                  <div className="account-update-modal-notice">
                    <SvgIcon name="shield" size={16} />
                    <div>
                      <strong>Administrative Verification:</strong> Modifications to your primary email and sector category require administrator approval and will not take effect immediately.
                    </div>
                  </div>
                )
              )}

              <div className="modal-field">
                <label>Current Email</label>
                <input type="email" value={profile?.email || ''} readOnly />
              </div>
              <div className="modal-field">
                <label>New Email</label>
                <div style={{ position: 'relative' }}>
                  <input type="email" placeholder="Enter new email address" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                  {isCheckingDomain && (
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#94a3b8' }}>⏳</span>
                  )}
                </div>
                {emailDomainMatch && (
                  <div className="email-domain-match-banner" style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
                    border: '1px solid #86efac',
                    fontSize: '12.5px',
                    color: '#166534',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    animation: 'fadeIn 0.3s ease-out'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                    <span>
                      {emailDomainMatch.orgs.length === 1
                        ? <>Organisation auto-detected: <strong>{emailDomainMatch.orgs[0].OrganisationName}</strong></>
                        : <>Domain <strong>@{emailDomainMatch.domain}</strong> matched {emailDomainMatch.orgs.length} organisations — please select below</>
                      }
                    </span>
                  </div>
                )}
              </div>
              <div className="modal-field">
                <label>Confirm New Email</label>
                <input type="email" placeholder="Confirm new email address" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} />
              </div>

              {/* Sector selection inside Email Modal */}
              {!isStaff && (
                <div className="modal-field">
                  <label>Sector</label>
                  <select value={orgSector} onChange={(e) => { setOrgSector(e.target.value); setSelectedOrgId(''); setSelectedOrg(null); setIsCreatingNewOrg(false); setNewOrgName(''); setProjectCode(''); setEmailError(''); setOrgError(''); if (['Government','Private','Student'].includes(e.target.value)) fetchOrgs(e.target.value); }}>
                    <option value="" disabled>Select Sector</option>
                    <option value="Government">Government</option>
                    <option value="Private">Private</option>
                    <option value="Student">Student</option>
                  </select>
                </div>
              )}

              {!isStaff && renderOrgSelectorFields()}

              {emailError && <p className="modal-field-error">{emailError}</p>}
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn-primary" 
                onClick={handleEmailUpdate} 
                disabled={emailSubmitting || (!isStaff && accountUpdateRequest?.Status === 'Pending')}
                data-testid="submit-account-update-btn"
              >
                {emailSubmitting ? 'Submitting...' : (isStaff ? 'Update' : 'Submit for Admin Approval')}
              </button>
              <button className="modal-btn-secondary" onClick={() => setEmailModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Organisation Modal */}
      {orgModal && (
        <div className="profile-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setOrgModal(false); }}>
          <div className="profile-modal-card">
            <div className="modal-header">
              <h3>Edit Email & Organisation</h3>
              <button className="modal-close-btn" onClick={() => setOrgModal(false)}><SvgIcon name="x" size={20} /></button>
            </div>
            <div className="modal-body">
              {!isStaff && (
                accountUpdateRequest?.Status === 'Pending' ? (
                  <div className="account-update-modal-notice warning" data-testid="modal-pending-warning">
                    <span>⚠️</span>
                    <div>
                      <strong>Pending Request Under Review:</strong> You already have an account-update request submitted on {formatDateTimeString(accountUpdateRequest.SubmittedAt)} awaiting administrator review. Submitting another change is disabled until processed.
                    </div>
                  </div>
                ) : (
                  <div className="account-update-modal-notice">
                    <SvgIcon name="shield" size={16} />
                    <div>
                      <strong>Administrative Verification:</strong> Modifications to your primary email and sector category require administrator approval and will not take effect immediately.
                    </div>
                  </div>
                )
              )}

              {/* Email fields inside Organisation Modal */}
              <div className="modal-field">
                <label>Current Email</label>
                <input type="email" value={profile?.email || ''} readOnly />
              </div>
              <div className="modal-field">
                <label>New Email</label>
                <div style={{ position: 'relative' }}>
                  <input type="email" placeholder="Enter new email address" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                  {isCheckingDomain && (
                    <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#94a3b8' }}>⏳</span>
                  )}
                </div>
                {emailDomainMatch && (
                  <div className="email-domain-match-banner" style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
                    border: '1px solid #86efac',
                    fontSize: '12.5px',
                    color: '#166534',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    animation: 'fadeIn 0.3s ease-out'
                  }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                    <span>
                      {emailDomainMatch.orgs.length === 1
                        ? <>Organisation auto-detected: <strong>{emailDomainMatch.orgs[0].OrganisationName}</strong></>
                        : <>Domain <strong>@{emailDomainMatch.domain}</strong> matched {emailDomainMatch.orgs.length} organisations — please select below</>
                      }
                    </span>
                  </div>
                )}
              </div>
              <div className="modal-field">
                <label>Confirm New Email</label>
                <input type="email" placeholder="Confirm new email address" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} />
              </div>

              <div className="modal-field">
                <label>Sector</label>
                <select value={orgSector} onChange={(e) => { setOrgSector(e.target.value); setSelectedOrgId(''); setSelectedOrg(null); setIsCreatingNewOrg(false); setNewOrgName(''); setProjectCode(''); setEmailError(''); setOrgError(''); if (['Government','Private','Student'].includes(e.target.value)) fetchOrgs(e.target.value); }}>
                  <option value="" disabled>Select Sector</option>
                  <option value="Government">Government</option>
                  <option value="Private">Private</option>
                  <option value="Student">Student</option>
                </select>
              </div>

              {renderOrgSelectorFields()}

              {orgError && <p className="modal-field-error">{orgError}</p>}
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn-primary" 
                onClick={handleOrgUpdate} 
                disabled={orgSubmitting || (!isStaff && accountUpdateRequest?.Status === 'Pending')}
                data-testid="submit-account-update-btn"
              >
                {orgSubmitting ? 'Submitting...' : (isStaff ? 'Update' : 'Submit for Admin Approval')}
              </button>
              <button className="modal-btn-secondary" onClick={() => setOrgModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}


      {/* Supervisor Modal */}
      {supervisorModal && (
        <div className="profile-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSupervisorModal(false); }}>
          <div className="profile-modal-card">
            <div className="modal-header">
              <h3>Edit Supervisor Details</h3>
              <button className="modal-close-btn" onClick={() => setSupervisorModal(false)}><SvgIcon name="x" size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label>Current Supervisor Name</label>
                <input type="text" value={profile?.supervisorName || 'N/A'} readOnly />
              </div>
              <div className="modal-field">
                <label>New Supervisor Name</label>
                <input type="text" placeholder="Enter new supervisor name" value={newSupervisorName} onChange={(e) => setNewSupervisorName(e.target.value)} />
              </div>
              <div className="modal-field">
                <label>Current Supervisor Email</label>
                <input type="text" value={profile?.supervisorEmail || 'N/A'} readOnly />
              </div>
              <div className="modal-field">
                <label>New Supervisor Email</label>
                <input type="email" placeholder="Enter new supervisor email" value={newSupervisorEmail} onChange={(e) => setNewSupervisorEmail(e.target.value)} />
              </div>
              {supervisorError && <p className="modal-field-error">{supervisorError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn-primary" onClick={handleSupervisorUpdate} disabled={supervisorSubmitting}>{supervisorSubmitting ? 'Updating...' : 'Update Supervisor'}</button>
              <button className="modal-btn-secondary" onClick={() => setSupervisorModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Number Modal */}
      {contactModal && (
        <div className="profile-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setContactModal(false); }}>
          <div className="profile-modal-card">
            <div className="modal-header">
              <h3>Edit Contact Number</h3>
              <button className="modal-close-btn" onClick={() => setContactModal(false)}><SvgIcon name="x" size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label>Current Contact Number</label>
                <input type="text" value={profile?.contact || ''} readOnly />
              </div>
              <div className="modal-field">
                <label>New Contact Number</label>
                <input type="text" placeholder="e.g. 012-3456789" value={newContact} onChange={(e) => setNewContact(e.target.value)} />
              </div>
              {contactError && <p className="modal-field-error">{contactError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn-primary" onClick={handleContactUpdate} disabled={contactSubmitting}>{contactSubmitting ? 'Updating...' : 'Update Contact'}</button>
              <button className="modal-btn-secondary" onClick={() => setContactModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Full Name Modal */}
      {fullNameModal && (
        <div className="profile-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setFullNameModal(false); }}>
          <div className="profile-modal-card">
            <div className="modal-header">
              <h3>Edit Full Name</h3>
              <button className="modal-close-btn" onClick={() => setFullNameModal(false)}><SvgIcon name="x" size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label>Current Full Name</label>
                <input type="text" value={profile?.fullName || ''} readOnly style={{ backgroundColor: '#f1f5f9', color: '#64748b', cursor: 'not-allowed' }} />
              </div>
              <div className="modal-field">
                <label>New Full Name</label>
                <input 
                  type="text" 
                  placeholder="Enter new full name" 
                  value={newFullName} 
                  onChange={(e) => setNewFullName(e.target.value)} 
                />
              </div>
              {fullNameError && <p className="modal-field-error">{fullNameError}</p>}
            </div>
            <div className="modal-footer">
              <button className="modal-btn-primary" onClick={handleFullNameUpdate} disabled={fullNameSubmitting}>
                {fullNameSubmitting ? 'Updating...' : 'Update Name'}
              </button>
              <button className="modal-btn-secondary" onClick={() => setFullNameModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Accuracy Confirmation Modal */}
      <ProfileAccuracyWarningModal
        isOpen={showAccuracyModal}
        onClose={() => setShowAccuracyModal(false)}
        onConfirm={handleConfirmSaveProfile}
        isSubmitting={isSavingProfile}
      />
    </div>
  );
};

export default UserAccount;
